/**
 * ResumoComparativoReport - Inline display for Resumo Comparativo
 *
 * Special layout: info cards + setores table + last 3 counts comparison
 */
import React from 'react';

interface Props {
  data: any[];    // contagens array
  loading: boolean;
  meta: any;      // full response (hospital, auditores, setores, contagens, etc)
}

export const ResumoComparativoReport: React.FC<Props> = ({ data, loading, meta }) => {
  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        ))}
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        Nenhum dado disponível.
      </div>
    );
  }

  const { hospital, cliente, auditores, observacao, lacres, setores, contagens } = meta;

  return (
    <div className="space-y-6">
      {/* Info Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <InfoCard label="Hospital / Cliente" value={`${hospital || 'N/A'} — ${cliente || 'N/A'}`} />
        <InfoCard label="Cód. no cliente" value={meta.codigo_no_cliente} />
        <InfoCard label="Auditor(es)" value={auditores} />
        <InfoCard label="Data" value={meta.data} />
        <InfoCard label="Observação" value={observacao} />
        <InfoCard label="Lacre(s)" value={lacres} />
      </div>

      {/* Hospital Address */}
      {(meta.hospital_endereco || meta.hospital_cidade) && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Endereço do Hospital</h4>
          <p className="text-sm text-gray-900 dark:text-gray-100">
            {meta.hospital_endereco}
            {meta.hospital_bairro && ` - ${meta.hospital_bairro}`}
            {meta.hospital_cidade && ` - ${meta.hospital_cidade}`}
            {meta.hospital_estado && `/${meta.hospital_estado}`}
            {meta.hospital_cep && ` - CEP: ${meta.hospital_cep}`}
          </p>
          {meta.hospital_telefone && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Tel: {meta.hospital_telefone}</p>
          )}
        </div>
      )}

      {/* Setores Inspecionados */}
      {setores && setores.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Setores Inspecionados</h4>
          <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Setor</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Responsável</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Telefone</th>
                </tr>
              </thead>
              <tbody>
                {setores.map((s: any, i: number) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{s.setor || '-'}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{s.responsavel || '-'}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{s.telefone || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Últimas 3 Contagens Comparison */}
      {/* Story 2.2: backend agora filtra status='EXECUTADA' apenas. Quando
          o histórico tem menos de 2 EXECUTADAS anteriores, completamos
          com placeholders para manter o layout 3-col consistente. */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          Dados das Últimas {contagens?.length || 0} Contagens
        </h4>
        {contagens && contagens.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {contagens.map((c: any, i: number) => (
              <div
                key={i}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{c.label}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{c.data}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <StatRow label="Total Sistema" value={fmt(c.total_sistema)} />
                  <StatRow label="Total Contado" value={fmt(c.total_contado)} />
                  <StatRow label="Sobras" value={fmt(c.sobras)} color="green" />
                  <StatRow label="Faltas" value={fmt(c.faltas)} color="red" />
                  <StatRow label="Segregados" value={fmt(c.segregados)} />
                  <StatRow label="Acuracidade" value={`${c.acuracidade}%`} color={c.acuracidade >= 95 ? 'green' : c.acuracidade >= 80 ? 'yellow' : 'red'} />
                  <div className="border-t border-gray-100 dark:border-gray-700 pt-2 mt-2">
                    <StatRow label="Auditor(es)" value={c.auditores} />
                    <StatRow label="Lacre(s)" value={c.lacres} />
                    <StatRow label="Ordem Coleta" value={c.ordem_coleta} />
                    <StatRow label="ID" value={`#${c.id}`} />
                  </div>
                </div>
              </div>
            ))}
            {/* Story 2.2: placeholders quando vier <3 contagens executadas */}
            {Array.from({ length: Math.max(0, 3 - contagens.length) }).map((_, i) => (
              <EmptyContagemCard key={`empty-${i}`} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[0, 1, 2].map(i => <EmptyContagemCard key={`empty-${i}`} />)}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Story 2.2: card placeholder quando não há contagem EXECUTADA anterior.
 * Substitui o comportamento antigo de vazar agendamentos futuros.
 */
function EmptyContagemCard() {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 flex flex-col items-center justify-center text-center min-h-[200px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 w-8 text-gray-400 dark:text-gray-600 mb-2"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
        Nenhuma contagem executada anterior
      </p>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
        Histórico aparecerá aqui após a primeira contagem executada
      </p>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{label}</span>
      <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{value || 'N/A'}</p>
    </div>
  );
}

function StatRow({ label, value, color }: { label: string; value: string; color?: string }) {
  const colorCls = color === 'green'
    ? 'text-green-600 dark:text-green-400'
    : color === 'red'
      ? 'text-red-600 dark:text-red-400'
      : color === 'yellow'
        ? 'text-yellow-600 dark:text-yellow-400'
        : 'text-gray-900 dark:text-gray-100';

  return (
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <span className={`font-medium ${colorCls}`}>{value}</span>
    </div>
  );
}

function fmt(val: number | undefined): string {
  if (val === undefined || val === null) return '0';
  return Number(val).toLocaleString('pt-BR');
}
